'use client';

import React, { useState, useRef, useCallback, useId, useEffect } from 'react';
import './Slider.css';

export interface SliderProps {
  /** Minimum value (for continuous mode) */
  min?: number;
  /** Maximum value (for continuous mode) */
  max?: number;
  /** Step increment (for continuous mode) */
  step?: number;
  /** Discrete points array - when provided, slider snaps to these values only */
  points?: number[];
  /** Controlled value */
  value?: number;
  /** Uncontrolled default value */
  defaultValue?: number;
  /** Called when value changes */
  onChange?: (value: number) => void;
  /** Format value for display (e.g., 1024 → '1GB') */
  formatValue?: (value: number) => string;
  /** Label displayed above the slider */
  label?: React.ReactNode;
  /** Position of the value display */
  valuePosition?: 'left' | 'right' | 'above' | 'below' | 'none';
  /** Whether the slider is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show tick marks at each step/point */
  showTicks?: boolean;
  /** Custom class for container */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

/**
 * Get the closest point index for a given value
 */
const getClosestPointIndex = (value: number, points: number[]): number => {
  let closestIndex = 0;
  let closestDistance = Math.abs(points[0] - value);

  for (let i = 1; i < points.length; i++) {
    const distance = Math.abs(points[i] - value);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = i;
    }
  }

  return closestIndex;
};

/**
 * Clamp a value between min and max
 */
const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Round value to nearest step
 */
const roundToStep = (value: number, min: number, step: number): number => {
  const steps = Math.round((value - min) / step);
  return min + steps * step;
};

export const Slider: React.FC<SliderProps> = ({
  min = 0,
  max = 100,
  step = 1,
  points,
  value: controlledValue,
  defaultValue,
  onChange,
  formatValue,
  label,
  valuePosition = 'right',
  disabled = false,
  size = 'md',
  showTicks = false,
  className = '',
  style,
}) => {
  const generatedId = useId();
  const sliderId = `hi-ui-slider-${generatedId}`;
  const labelId = `${sliderId}-label`;

  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // Determine mode and effective min/max
  const isPointsMode = points && points.length > 0;
  const effectiveMin = isPointsMode ? 0 : min;

  // Get initial value
  const getInitialValue = (): number => {
    if (controlledValue !== undefined) {
      return isPointsMode ? getClosestPointIndex(controlledValue, points) : controlledValue;
    }
    if (defaultValue !== undefined) {
      return isPointsMode ? getClosestPointIndex(defaultValue, points) : defaultValue;
    }
    return effectiveMin;
  };

  const [internalValue, setInternalValue] = useState(getInitialValue);

  // Determine if controlled
  const isControlled = controlledValue !== undefined;

  // Get current value (index for points mode, actual value for continuous)
  const getCurrentIndex = useCallback((): number => {
    if (isControlled) {
      return isPointsMode ? getClosestPointIndex(controlledValue!, points!) : controlledValue!;
    }
    return internalValue;
  }, [isControlled, controlledValue, internalValue, isPointsMode, points]);

  // Get the actual display value
  const getDisplayValue = useCallback((): number => {
    const idx = getCurrentIndex();
    return isPointsMode ? points![Math.round(clamp(idx, 0, points!.length - 1))] : idx;
  }, [getCurrentIndex, isPointsMode, points]);

  // Calculate percentage for positioning
  const getPercentage = useCallback((): number => {
    const idx = getCurrentIndex();
    if (isPointsMode) {
      return points!.length > 1 ? (idx / (points!.length - 1)) * 100 : 0;
    }
    return max !== min ? ((idx - min) / (max - min)) * 100 : 0;
  }, [getCurrentIndex, isPointsMode, points, min, max]);

  // Convert position to value
  const positionToValue = useCallback(
    (clientX: number): number => {
      const rect = trackRef.current!.getBoundingClientRect();
      const percent = clamp((clientX - rect.left) / rect.width, 0, 1);

      if (isPointsMode) {
        const index = Math.round(percent * (points!.length - 1));
        return points![index];
      }

      const rawValue = min + percent * (max - min);
      return roundToStep(clamp(rawValue, min, max), min, step);
    },
    [isPointsMode, points, min, max, step]
  );

  // Update value
  const updateValue = useCallback(
    (newValue: number) => {
      const idx = isPointsMode ? getClosestPointIndex(newValue, points!) : newValue;

      if (!isControlled) {
        setInternalValue(idx);
      }

      const actualValue = isPointsMode ? points![idx] : newValue;
      onChange?.(actualValue);
    },
    [isControlled, isPointsMode, points, onChange]
  );

  // Handle mouse/touch start
  const handleStart = useCallback(
    (clientX: number) => {
      if (disabled) return;

      isDragging.current = true;
      const newValue = positionToValue(clientX);
      updateValue(newValue);
    },
    [disabled, positionToValue, updateValue]
  );

  // Handle mouse/touch move
  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging.current || disabled) return;
      updateValue(positionToValue(clientX));
    },
    [disabled, positionToValue, updateValue]
  );

  // Handle mouse/touch end
  const handleEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
  }, []);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleStart(e.clientX);
    },
    [handleStart]
  );

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleStart(e.touches[0].clientX);
    },
    [handleStart]
  );

  // Global event listeners for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleMouseUp = () => handleEnd();
    const handleTouchMove = (e: globalThis.TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    };
    const handleTouchEnd = () => handleEnd();

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMove, handleEnd]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      let newValue: number;
      const currentIdx = getCurrentIndex();
      const actualValue = getDisplayValue();

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          if (isPointsMode) {
            const newIdx = Math.min(currentIdx + 1, points!.length - 1);
            newValue = points![newIdx];
          } else {
            newValue = Math.min(actualValue + step, max);
          }
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          if (isPointsMode) {
            const newIdx = Math.max(currentIdx - 1, 0);
            newValue = points![newIdx];
          } else {
            newValue = Math.max(actualValue - step, min);
          }
          break;
        case 'Home':
          e.preventDefault();
          newValue = isPointsMode ? points![0] : min;
          break;
        case 'End':
          e.preventDefault();
          newValue = isPointsMode ? points![points!.length - 1] : max;
          break;
        case 'PageUp':
          e.preventDefault();
          if (isPointsMode) {
            const bigStep = Math.max(1, Math.floor(points!.length / 10));
            const newIdx = Math.min(currentIdx + bigStep, points!.length - 1);
            newValue = points![newIdx];
          } else {
            const bigStep = (max - min) / 10;
            newValue = Math.min(actualValue + bigStep, max);
          }
          break;
        case 'PageDown':
          e.preventDefault();
          if (isPointsMode) {
            const bigStep = Math.max(1, Math.floor(points!.length / 10));
            const newIdx = Math.max(currentIdx - bigStep, 0);
            newValue = points![newIdx];
          } else {
            const bigStep = (max - min) / 10;
            newValue = Math.max(actualValue - bigStep, min);
          }
          break;
        default:
          return;
      }

      updateValue(newValue);
    },
    [disabled, getCurrentIndex, getDisplayValue, isPointsMode, points, step, min, max, updateValue]
  );

  // Format the display value
  const displayValue = getDisplayValue();
  const formattedValue = formatValue ? formatValue(displayValue) : String(displayValue);
  const percentage = getPercentage();

  // Build class names
  const containerClasses = [
    'hi-ui-slider',
    `hi-ui-slider--${size}`,
    `hi-ui-slider--value-${valuePosition}`,
    disabled ? 'hi-ui-slider--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Value display element
  const valueDisplay =
    valuePosition !== 'none' ? <span className="hi-ui-slider__value">{formattedValue}</span> : null;

  return (
    <div className={containerClasses} style={style}>
      {label && (
        <label id={labelId} className="hi-ui-slider__label">
          {label}
        </label>
      )}

      <div className="hi-ui-slider__wrapper">
        {(valuePosition === 'left' || valuePosition === 'above') && valueDisplay}

        <div
          ref={trackRef}
          className="hi-ui-slider__track"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {showTicks && (
            <div className="hi-ui-slider__ticks" aria-hidden="true">
              {isPointsMode
                ? points!.map((_, index) => (
                    <span
                      key={index}
                      className="hi-ui-slider__tick"
                      style={{
                        left: `${(index / (points!.length - 1)) * 100}%`,
                      }}
                    />
                  ))
                : Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, index) => (
                    <span
                      key={index}
                      className="hi-ui-slider__tick"
                      style={{
                        left: `${(index / Math.floor((max - min) / step)) * 100}%`,
                      }}
                    />
                  ))}
            </div>
          )}
          <div className="hi-ui-slider__fill" style={{ width: `${percentage}%` }} />
          <div
            role="slider"
            tabIndex={disabled ? -1 : 0}
            className="hi-ui-slider__thumb"
            style={{ left: `${percentage}%` }}
            aria-valuemin={isPointsMode ? points![0] : min}
            aria-valuemax={isPointsMode ? points![points!.length - 1] : max}
            aria-valuenow={displayValue}
            aria-valuetext={formattedValue}
            aria-disabled={disabled || undefined}
            aria-labelledby={label ? labelId : undefined}
            onKeyDown={handleKeyDown}
          />
        </div>

        {(valuePosition === 'right' || valuePosition === 'below') && valueDisplay}
      </div>
    </div>
  );
};
